import {
  PARTY,
  JURISDICTION,
  QS,
  ENDORSEMENT,
  ALPHABET,
  ENDORSEMENT_ORDER,
} from '../components/election/constants.js';
import { Contest, Candidate, Measure } from '../components/election/data_objs.js';

const defaultOrder = ALPHABET;

// California June 2, 2026 Primary Election
// Filing deadline: March 6, 2026 — candidate lists are preliminary
// Top-two nonpartisan primary: top two vote-getters advance to Nov general

export const ca_june26_contests = {
  // ── Statewide Offices ──────────────────────────────────────────────

  'Governor': Contest(
    JURISDICTION.STATE,
    'Governor',
    [
      // Democrats
      Candidate('Xavier Becerra', PARTY.DEM, 'Former U.S. Secretary of Health and Human Services', QS.NOT_SENT),
      Candidate('Ian Calderon', PARTY.DEM, 'Former Assemblymember', QS.NOT_SENT),
      Candidate('Matt Mahan', PARTY.DEM, 'Mayor of San Jose', QS.NOT_SENT),
      Candidate('Katie Porter', PARTY.DEM, 'Former U.S. Representative', QS.NOT_SENT),
      Candidate('Tom Steyer', PARTY.DEM, 'Investor / Climate Activist', QS.NOT_SENT),
      Candidate('Eric Swalwell', PARTY.DEM, 'U.S. Representative', QS.NOT_SENT),
      Candidate('Tony Thurmond', PARTY.DEM, 'State Superintendent of Public Instruction', QS.NOT_SENT),
      Candidate('Antonio Villaraigosa', PARTY.DEM, 'Former Mayor of Los Angeles', QS.NOT_SENT),
      Candidate('Betty Yee', PARTY.DEM, 'Former State Controller', QS.NOT_SENT),
      // Republicans
      Candidate('Chad Bianco', PARTY.REP, 'Riverside County Sheriff', QS.NOT_SENT),
      Candidate('Steve Hilton', PARTY.REP, 'Former Fox News Host', QS.NOT_SENT),
      // Green Party
      Candidate('Butch Ware', PARTY.GRN, 'Professor', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'Lieutenant Governor': Contest(
    JURISDICTION.STATE,
    'Lieutenant Governor',
    [
      // Incumbent Eleni Kounalakis is term-limited; running for Treasurer instead
      Candidate('Josh Fryday', PARTY.DEM, 'Chief Service Officer for Governor Newsom', QS.NOT_SENT),
      Candidate('Fiona Ma', PARTY.DEM, 'State Treasurer', QS.NOT_SENT),
      Candidate('Michael Tubbs', PARTY.DEM, 'Former Mayor of Stockton', QS.NOT_SENT),
      Candidate('Mike Schaefer', PARTY.DEM, 'Member, State Board of Equalization', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'Attorney General': Contest(
    JURISDICTION.STATE,
    'Attorney General',
    [
      Candidate('Rob Bonta', PARTY.DEM, 'Incumbent Attorney General', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'Secretary of State': Contest(
    JURISDICTION.STATE,
    'Secretary of State',
    [
      Candidate('Shirley Weber', PARTY.DEM, 'Incumbent Secretary of State', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'State Controller': Contest(
    JURISDICTION.STATE,
    'State Controller',
    [
      Candidate('Malia Cohen', PARTY.DEM, 'Incumbent State Controller', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'State Treasurer': Contest(
    JURISDICTION.STATE,
    'State Treasurer',
    [
      // Incumbent Fiona Ma is term-limited
      Candidate('Anna Caballero', PARTY.DEM, 'State Senator', QS.NOT_SENT),
      Candidate('Eleni Kounalakis', PARTY.DEM, 'Lieutenant Governor', QS.NOT_SENT),
      Candidate('Tony Vazquez', PARTY.DEM, 'Member, State Board of Equalization; Former Mayor of Santa Monica', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'Insurance Commissioner': Contest(
    JURISDICTION.STATE,
    'Insurance Commissioner',
    [
      // Incumbent Ricardo Lara is term-limited
      Candidate('Ben Allen', PARTY.DEM, 'State Senator', QS.NOT_SENT),
      Candidate('Steven Bradford', PARTY.DEM, 'Former State Senator', QS.NOT_SENT),
      Candidate('Patrick Wolff', PARTY.DEM, 'Business Executive / Chess Grandmaster', QS.NOT_SENT),
      Candidate('Stacy Korsgaden', PARTY.REP, 'Insurance Agent', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'Superintendent of Public Instruction': Contest(
    JURISDICTION.STATE,
    'Superintendent of Public Instruction',
    [
      // Incumbent Tony Thurmond is term-limited; running for Governor
      // Nonpartisan office — candidates do not list party on ballot
      Candidate('Al Muratsuchi', PARTY.NP, 'Assemblymember', QS.NOT_SENT),
      Candidate('Josh Newman', PARTY.NP, 'Former State Senator', QS.NOT_SENT),
      Candidate('Anthony Rendon', PARTY.NP, 'Former Assembly Speaker', QS.NOT_SENT),
      Candidate('Sonja Shaw', PARTY.NP, 'School Board President, Chino Valley USD', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  // ── Federal — SF Congressional District ────────────────────────────

  'U.S. House — District 11': Contest(
    JURISDICTION.FEDERAL,
    'U.S. House — District 11',
    [
      // Open seat — Nancy Pelosi retiring Jan 2027
      Candidate('Saikat Chakrabarti', PARTY.DEM, 'Entrepreneur / Former Congressional Chief of Staff', QS.NOT_SENT),
      Candidate('Connie Chan', PARTY.DEM, 'San Francisco Supervisor', QS.NOT_SENT),
      Candidate('Scott Wiener', PARTY.DEM, 'State Senator', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  // ── State Legislature — SF Districts ───────────────────────────────
  // Note: State Senate District 11 (Wiener) is NOT up in 2026 — odd-numbered
  // districts were last elected in 2024 and serve until 2028.

  'State Assembly — District 17': Contest(
    JURISDICTION.STATE,
    'State Assembly — District 17',
    [
      Candidate('Matt Haney', PARTY.DEM, 'Incumbent Assemblymember', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  'State Assembly — District 19': Contest(
    JURISDICTION.STATE,
    'State Assembly — District 19',
    [
      Candidate('Catherine Stefani', PARTY.DEM, 'Incumbent Assemblymember', QS.NOT_SENT),
    ],
    defaultOrder,
  ),

  // ── Statewide Ballot Measures ──────────────────────────────────────
  // Three measures qualified as of Jan 2026; more may qualify before the election

  'ACA 13': Measure(
    JURISDICTION.STATE,
    'Voting Thresholds for Initiatives',
    'ACA 13',
    ENDORSEMENT.COMING_SOON,
    null,
    'Requires initiatives proposing supermajority thresholds to pass by the same threshold being proposed.',
  ),

  'SCA 1': Measure(
    JURISDICTION.STATE,
    'Recall Elections: Eliminate Successor Election',
    'SCA 1',
    ENDORSEMENT.COMING_SOON,
    null,
    'Eliminates the successor election at state officer recall elections.',
  ),

  'SB 42': Measure(
    JURISDICTION.STATE,
    'California Fair Elections Act of 2026',
    'SB 42',
    ENDORSEMENT.COMING_SOON,
    null,
    'Authorizes public financing for state and local election campaigns.',
  ),
};
